import React, { useCallback, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import html2canvas from 'html2canvas';
import { saveBlobDownload } from '../../../shared/utils/saveBlobDownload';

const cardActionSx = {
  flexShrink: 0,
  textTransform: 'none',
  fontWeight: 600,
  color: 'rgba(var(--color-text-rgb), 0.72)',
  minWidth: 0,
  px: 1,
} as const;

function parseCssColor(color: string): { r: number; g: number; b: number; a: number } | null {
  const match = color.match(/rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*([\d.]+))?\s*\)/i);
  if (!match) return null;
  return {
    r: Number(match[1]),
    g: Number(match[2]),
    b: Number(match[3]),
    a: match[4] === undefined ? 1 : Number(match[4]),
  };
}

/** Chart cards use a faint tint, which html2canvas flattens onto white.
 *  Walk up to the dialog surface so a dark theme stays dark in the PNG. */
function opaqueBackground(el: HTMLElement): string {
  let node: HTMLElement | null = el;
  while (node) {
    const parsed = parseCssColor(getComputedStyle(node).backgroundColor);
    if (parsed && parsed.a >= 0.9) {
      return `rgb(${Math.round(parsed.r)}, ${Math.round(parsed.g)}, ${Math.round(parsed.b)})`;
    }
    node = node.parentElement;
  }
  const surface = getComputedStyle(document.documentElement).getPropertyValue('--color-bg').trim();
  return surface || '#ffffff';
}

async function downloadElementAsPng(el: HTMLElement, fileName: string): Promise<void> {
  const backgroundColor = opaqueBackground(el);

  const canvas = await html2canvas(el, {
    useCORS: true,
    scale: 2,
    backgroundColor,
    logging: false,
    imageTimeout: 0,
    ignoreElements: (node) =>
      node instanceof HTMLElement && node.dataset.html2canvasIgnore === 'true',
  });

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, 'image/png');
  });
  if (!blob) throw new Error('Failed to encode PNG');

  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  const datedFileName = `${fileName}-${yyyy}-${mm}-${dd}.png`;

  await saveBlobDownload(blob, datedFileName, 'image/png');
}

export const ChartDownloadImageButton: React.FC<{
  targetRef: React.RefObject<HTMLElement | null>;
  fileName: string;
}> = ({ targetRef, fileName }) => {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = useCallback(async () => {
    if (!targetRef.current || downloading) return;
    setDownloading(true);
    try {
      await downloadElementAsPng(targetRef.current, fileName);
    } catch (err) {
      console.error('Failed to download analytics chart PNG:', err);
    } finally {
      setDownloading(false);
    }
  }, [downloading, fileName, targetRef]);

  return (
    <Button
      data-html2canvas-ignore="true"
      size="small"
      variant="text"
      onClick={handleDownload}
      disabled={downloading}
      startIcon={downloading ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />}
      sx={cardActionSx}
    >
      Download Image
    </Button>
  );
};

/** Chart card with title row + optional Download Image (same pattern as CPD Analytics). */
export const AnalyticsDownloadableChartCard: React.FC<{
  title: string;
  /** Omit to hide the Download Image button. */
  fileName?: string;
  subtitle?: string;
  titleAdornment?: React.ReactNode;
  onTitleClick?: () => void;
  titleExpanded?: boolean;
  sx?: object;
  children: React.ReactNode;
}> = ({
  title,
  fileName,
  subtitle,
  titleAdornment,
  onTitleClick,
  titleExpanded,
  sx,
  children,
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const showTitleMargin = Boolean(subtitle) || titleExpanded !== false;

  return (
    <Box ref={ref} sx={sx}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1,
          mb: showTitleMargin ? 2 : 0,
        }}
      >
        <Box
          role={onTitleClick ? 'button' : undefined}
          tabIndex={onTitleClick ? 0 : undefined}
          onClick={onTitleClick}
          onKeyDown={onTitleClick ? (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onTitleClick();
            }
          } : undefined}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            minWidth: 0,
            cursor: onTitleClick ? 'pointer' : 'default',
            userSelect: onTitleClick ? 'none' : undefined,
            '&:focus-visible': onTitleClick
              ? { outline: '2px solid var(--color-accent)', outlineOffset: 2 }
              : undefined,
          }}
          aria-expanded={onTitleClick ? titleExpanded : undefined}
        >
          {titleAdornment}
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, color: 'var(--color-text-strong)', lineHeight: 1.3 }}
            >
              {title}
            </Typography>
            {subtitle ? (
              <Typography
                variant="body2"
                sx={{ color: 'rgba(var(--color-text-rgb), 0.72)', mt: 0.5 }}
              >
                {subtitle}
              </Typography>
            ) : null}
          </Box>
        </Box>
        {fileName ? <ChartDownloadImageButton targetRef={ref} fileName={fileName} /> : null}
      </Box>
      {children}
    </Box>
  );
};

